import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import {
  isProfileNameTitle,
  isProfileOccupation,
  isProfileSalutation,
  type OwnProfile,
  type ProfileUpdateInput,
  type ProfileVisibility,
} from "@/lib/profile/types";

type ProfileRow = {
  id: string;
  full_name: string;
  salutation: string | null;
  name_title: string | null;
  professional_title: string | null;
  occupation: string | null;
  phone: string | null;
  city: string | null;
  bio: string | null;
  registry_number: string | null;
  account_status: OwnProfile["accountStatus"];
  profile_visibility?: ProfileVisibility | null;
};

export async function getMyProfile(): Promise<OwnProfile | null> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user) {
    return null;
  }

  const { data, error } = await supabase.rpc("get_my_profile");

  if (error) {
    throw error;
  }

  if (!data) {
    return {
      id: user.id,
      email: user.email ?? null,
      fullName: user.email ?? "Felhasználó",
      salutation: null,
      nameTitle: null,
      professionalTitle: null,
      occupation: null,
      phone: null,
      city: null,
      bio: null,
      registryNumber: null,
      accountStatus: "pending",
      profileVisibility: "public",
    };
  }

  const row = data as ProfileRow;
  return {
    id: row.id,
    email: user.email ?? null,
    fullName: row.full_name,
    salutation: isProfileSalutation(row.salutation) ? row.salutation : null,
    nameTitle: isProfileNameTitle(row.name_title) ? row.name_title : null,
    professionalTitle: row.professional_title,
    occupation: isProfileOccupation(row.occupation) ? row.occupation : null,
    phone: row.phone,
    city: row.city,
    bio: row.bio,
    registryNumber: row.registry_number,
    accountStatus: row.account_status,
    profileVisibility:
      row.profile_visibility === "private" ? "private" : "public",
  };
}

export async function updateMyProfile(input: ProfileUpdateInput): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user) {
    throw new Error("A profil szerkesztéséhez be kell jelentkeznie.");
  }

  const { error } = await supabase
    .from("profiles")
    .update({
      full_name: input.fullName.trim(),
      salutation: input.salutation,
      name_title: input.nameTitle || null,
      occupation: input.occupation,
      professional_title: input.professionalTitle.trim() || null,
      phone: input.phone.trim() || null,
      city: input.city.trim() || null,
      bio: input.bio.trim() || null,
      registry_number: input.registryNumber.trim() || null,
      profile_visibility: input.profileVisibility,
      introduction_settings_completed: true,
    })
    .eq("id", user.id);

  if (error) {
    throw error;
  }
}

export async function completeFirstUseTour(): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user) {
    throw new Error("A bemutató befejezéséhez be kell jelentkeznie.");
  }

  const { error } = await supabase
    .from("profiles")
    .update({ onboarding_completed: true })
    .eq("id", user.id);

  if (error) {
    throw error;
  }
}
